import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { credentialsApi } from '../services/api';
import { Key, Search, Filter, Eye, Calendar, Tag } from 'lucide-react';

interface Credential {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  nodesAccess: string[];
}

export default function CredentialsPage() {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [filteredCredentials, setFilteredCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [types, setTypes] = useState<string[]>([]);

  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        const [credResponse, typesResponse] = await Promise.all([
          credentialsApi.getAll(),
          credentialsApi.getTypes(),
        ]);

        const creds = credResponse.data.data.credentials || [];
        setCredentials(creds);
        setFilteredCredentials(creds);

        const credTypes = typesResponse.data.data.types?.map((t: any) => t.type) || [];
        setTypes(credTypes);
      } catch (err: any) {
        console.error('Failed to fetch credentials:', err);
        setError(err.response?.data?.error?.message || 'Failed to load credentials');
      } finally {
        setLoading(false);
      }
    };

    fetchCredentials();
  }, []);

  useEffect(() => {
    let filtered = credentials;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          c.type.toLowerCase().includes(term)
      );
    }

    if (typeFilter) {
      filtered = filtered.filter((c) => c.type === typeFilter);
    }

    setFilteredCredentials(filtered);
  }, [searchTerm, typeFilter, credentials]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      httpHeaderAuth: 'bg-blue-500/20 text-blue-400',
      httpBasicAuth: 'bg-green-500/20 text-green-400',
      oAuth2Api: 'bg-purple-500/20 text-purple-400',
      postgres: 'bg-cyan-500/20 text-cyan-400',
      mysql: 'bg-orange-500/20 text-orange-400',
      aws: 'bg-yellow-500/20 text-yellow-400',
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <Key className="mx-auto text-gray-600 mb-4" size={48} />
        <h3 className="text-xl font-semibold text-white mb-2">Error Loading Credentials</h3>
        <p className="text-gray-400">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header with filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search credentials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="input pl-10 pr-8 appearance-none cursor-pointer"
            >
              <option value="">All Types</option>
              {types.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results count */}
      <p className="text-gray-400 text-sm mb-4">
        Showing {filteredCredentials.length} of {credentials.length} credentials
      </p>

      {/* Credentials grid */}
      {filteredCredentials.length === 0 ? (
        <div className="card text-center py-12">
          <Key className="mx-auto text-gray-600 mb-4" size={48} />
          <h3 className="text-xl font-semibold text-white mb-2">No Credentials Found</h3>
          <p className="text-gray-400">
            {credentials.length === 0
              ? 'No credentials have been stored yet.'
              : 'No credentials match your search criteria.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCredentials.map((credential) => (
            <Link
              key={credential.id}
              to={`/credentials/${credential.id}`}
              className="card hover:bg-gray-700/80 transition-colors group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="p-2 bg-brand-orange/20 rounded-lg">
                    <Key className="text-brand-orange" size={20} />
                  </div>
                  <h3 className="ml-3 text-white font-medium group-hover:text-brand-orange transition-colors">
                    {credential.name}
                  </h3>
                </div>
                <Eye className="text-gray-500 group-hover:text-brand-orange transition-colors" size={18} />
              </div>

              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Tag size={14} className="text-gray-500 mr-2" />
                  <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(credential.type)}`}>
                    {credential.type}
                  </span>
                </div>

                <div className="flex items-center text-sm text-gray-400">
                  <Calendar size={14} className="mr-2" />
                  Updated {formatDate(credential.updatedAt)}
                </div>

                {credential.nodesAccess.length > 0 && (
                  <p className="text-xs text-gray-500 truncate">
                    Used by: {credential.nodesAccess.slice(0, 2).join(', ')}
                    {credential.nodesAccess.length > 2 && ` +${credential.nodesAccess.length - 2} more`}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Info box */}
      <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
        <h4 className="text-yellow-400 font-medium mb-2">⚠️ Read-Only View</h4>
        <p className="text-gray-300 text-sm">
          Credentials are displayed with sensitive data masked for security.
          You can view credential details, but modifications must be requested through PHH Digital.
        </p>
      </div>
    </div>
  );
}
